import React from "react";
import { Button, DescriptionList } from "../../../../ui";
import { Callout } from "./ui-pending";
import "./BookingPreview.scss";
import type { PreviewDateEntry, PreviewScheduleResponse } from "./BookingPreview";

type MatchedSlot = { day: string; time: string };

export type BookingSuccessMode = "booked" | "assigned";

type BookingSuccessScreenProps = {
  mode: BookingSuccessMode;
  teacher: {
    firstName?: string;
    lastName?: string;
    payoutCategory?: string;
  };
  matchedSlots: MatchedSlot[];
  preview: PreviewScheduleResponse;
  scheduleType?: string;
  onDone: () => void;
  backLabel?: string;
};

function formatTime(time?: string): string {
  if (!time) return "";
  const [hStr, mStr] = String(time).split(":");
  let hour = parseInt(hStr, 10);
  const minutes = mStr || "00";
  if (Number.isNaN(hour)) return time;
  const meridiem = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${meridiem}`;
}

function formatShortDate(dateStr: string, dayOfWeek?: string): string {
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const dayShort = dayOfWeek
      ? dayOfWeek.slice(0, 1) + dayOfWeek.slice(1, 3).toLowerCase()
      : date.toLocaleDateString("en-GB", { weekday: "short" });
    const dayNum = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${dayShort}, ${dayNum} ${month} ${year}`;
  } catch {
    return dateStr;
  }
}

function formatRecurringSlot(slots: MatchedSlot[]): string {
  if (!slots.length) return "Not scheduled yet";
  return slots.map((s) => `Every ${s.day} – ${formatTime(s.time)}`).join("  ·  ");
}

function getFirstLastClass(dates: PreviewDateEntry[]): { first: string; last: string } {
  if (!dates.length) return { first: "—", last: "—" };
  const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return {
    first: formatShortDate(first.date, first.dayOfWeek),
    last: formatShortDate(last.date, last.dayOfWeek),
  };
}

const BookingSuccessScreen: React.FC<BookingSuccessScreenProps> = ({
  mode,
  teacher,
  matchedSlots,
  preview,
  scheduleType = "fixed",
  onDone,
  backLabel = "← Back to Find Teacher",
}) => {
  const teacherName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "Teacher";
  const level = teacher.payoutCategory || "";
  const teacherDisplay = level ? `${teacherName} · ${level}` : teacherName;

  const purchasedDates = preview.dates || [];
  const purchasedCount = preview.totalClasses ?? purchasedDates.length;
  const { first, last } = getFirstLastClass(purchasedDates);

  const isBooked = mode === "booked";
  const title = isBooked ? "All Classes Booked!" : "Teacher Assigned!";
  const subtitle = isBooked
    ? `${purchasedCount} classes scheduled`
    : "Teacher assigned. Classes can be booked separately.";

  return (
    <div className="booking-success">
      <Callout tone="success" title={title}>
        {subtitle}
      </Callout>

      <DescriptionList
        items={[
          { label: "Teacher", value: teacherDisplay },
          {
            label: "Recurring Slot",
            value: isBooked ? formatRecurringSlot(matchedSlots) : "Not scheduled yet",
          },
          { label: "First Class", value: isBooked ? first : "—" },
          { label: "Last Class", value: isBooked ? last : "—" },
          {
            label: "Schedule Type",
            value: scheduleType ? scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1) : "Fixed",
          },
          // ...(isBooked ? [{ label: "Total Classes Booked", value: String(totalBookedClasses) }] : []),
        ]}
      />

      <Button variant="primary" className="booking-success__done" onClick={onDone}>
        {backLabel}
      </Button>
    </div>
  );
};

export default BookingSuccessScreen;
